import React, { useEffect, useRef, useState } from 'react';
import StoryProgress from './StoryProgress';
import StorySlide from './StorySlide';
import { useStoryPlayback } from '../../hooks/useStoryPlayback';
import {
  StorySlide as StorySlideData,
  HighOddsWinnerData,
  UnitsProfitLastDayData,
} from '../../types/interfaces';
import { getMyStorySlides, markSlideSeen } from '../../services/storySlideService';
import './StoryViewer.scss';

const BG_HIGH_ODDS = 'linear-gradient(160deg, #0d1117 0%, #1a1f2e 50%, #0f1922 100%)';
const BG_UNITS = 'linear-gradient(160deg, #0d1117 0%, #1a2518 50%, #0f1a12 100%)';

function formatProfit(val: number): string {
  return `+${val % 1 === 0 ? val.toFixed(1) : val.toFixed(2)}u`;
}

const HighOddsSlideContent: React.FC<{ data: HighOddsWinnerData }> = ({ data }) => (
  <div className="slide-content slide-content--high-odds">
    <div className="slide-content__badge">High Odds Winner</div>
    {data.league_name && (
      <p className="slide-content__league">{data.league_name}</p>
    )}
    <p className="slide-content__profit">{formatProfit(data.profit_units)}</p>
    {data.home_team && data.away_team && (
      <p className="slide-content__match">
        {data.home_team} — {data.away_team}
      </p>
    )}
    <p className="slide-content__event">{data.event_label}</p>
    <p className="slide-content__odds">@ {data.odds.toFixed(2)}</p>
  </div>
);

const UnitsProfitSlideContent: React.FC<{ data: UnitsProfitLastDayData }> = ({ data }) => {
  const dateLabel = new Date(data.date).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  return (
    <div className="slide-content slide-content--units-profit">
      <div className="slide-content__badge">Yesterday's Profit</div>
      <p className="slide-content__league">{dateLabel}</p>
      <p className="slide-content__profit">{formatProfit(data.profit_units)}</p>
      <p className="slide-content__match">
        {data.wins}W / {data.losses}L &nbsp;·&nbsp; {data.total_picks} picks
      </p>
    </div>
  );
};

interface StoryViewerProps {
  isOpen: boolean;
  onClose: () => void;
}

const StoryViewer: React.FC<StoryViewerProps> = ({ isOpen, onClose }) => {
  const [slides, setSlides] = useState<StorySlideData[]>([]);
  const [loading, setLoading] = useState(true);
  const seenRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    getMyStorySlides()
      .then(setSlides)
      .catch(() => setSlides([]))
      .finally(() => setLoading(false));
  }, []);

  const { currentIndex, progress, goNext, goPrev, pause, resume } = useStoryPlayback(
    slides.length || 1,
    5000,
    onClose
  );

  const pressStartRef = useRef<number>(0);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, goNext, goPrev]);

  useEffect(() => {
    const slide = slides[currentIndex];
    if (!slide || seenRef.current.has(slide.id)) return;
    seenRef.current.add(slide.id);
    if (!slide.seen) {
      markSlideSeen(slide.id).catch(() => {});
    }
  }, [currentIndex, slides]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleZoneDown = () => {
    pressStartRef.current = Date.now();
    pause();
  };

  const handlePrevUp = () => {
    const held = Date.now() - pressStartRef.current;
    resume();
    if (held < 300) goPrev();
  };

  const handleNextUp = () => {
    const held = Date.now() - pressStartRef.current;
    resume();
    if (held < 300) goNext();
  };

  const handleZoneLeave = () => resume();

  const renderSlideContent = (slide: StorySlideData) => {
    if (!slide.data) return null;
    if (slide.type === 'HIGH_ODDS_WINNER') {
      return <HighOddsSlideContent data={slide.data as HighOddsWinnerData} />;
    }
    if (slide.type === 'UNITS_PROFIT_LAST_DAY') {
      return <UnitsProfitSlideContent data={slide.data as UnitsProfitLastDayData} />;
    }
    return null;
  };

  const slideBackground = (slide: StorySlideData) =>
    slide.type === 'HIGH_ODDS_WINNER' ? BG_HIGH_ODDS : BG_UNITS;

  return (
    <div className="story-viewer-backdrop" onClick={handleBackdropClick}>
      <div className="story-viewer">
        {loading ? (
          <div className="story-viewer__loading">
            <span className="story-viewer__loading-dot" />
            <span className="story-viewer__loading-dot" />
            <span className="story-viewer__loading-dot" />
          </div>
        ) : slides.length === 0 ? (
          <div className="story-viewer__empty">
            <p>No stories yet</p>
          </div>
        ) : (
          <>
            <StoryProgress total={slides.length} current={currentIndex} progress={progress} />
            <div className="story-viewer__slides">
              <div
                className="story-viewer__track"
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
              >
                {slides.map((slide) => (
                  <StorySlide key={slide.id} background={slideBackground(slide)}>
                    {renderSlideContent(slide)}
                  </StorySlide>
                ))}
              </div>
            </div>
          </>
        )}

        <button className="story-viewer__close" onClick={onClose} aria-label="Close story">
          ×
        </button>

        <button
          className="story-viewer__tap story-viewer__tap--prev"
          onPointerDown={handleZoneDown}
          onPointerUp={handlePrevUp}
          onPointerLeave={handleZoneLeave}
          aria-label="Previous slide"
        />
        <button
          className="story-viewer__tap story-viewer__tap--next"
          onPointerDown={handleZoneDown}
          onPointerUp={handleNextUp}
          onPointerLeave={handleZoneLeave}
          aria-label="Next slide"
        />
      </div>
    </div>
  );
};

export default StoryViewer;
